import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Card, Empty, Button } from 'antd';
import { FullscreenExitOutlined } from '@ant-design/icons';
// import { ThunderboltOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import type { Theme } from '../../contexts/themeTypes';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useSelectedTimestamp } from '../../contexts/useSelectedTimestamp';
import { isEventActive as checkEventActive, getEventSource } from '../../utils/eventSource';
import { isTioMapProviderSupported, mapTileProviderForObservationsState } from './tioMapProviderPolicy';
import { useCollectionFreshness } from '../../hooks/useCollectionFreshness';
import { Timeline } from '../../components/Timeline/Timeline';
import { TioForecastPanel } from './TioForecastPanel';
import { MinutelyForecastPanel } from './MinutelyForecastPanel';
import { MapSettingsPanel } from './MapSettingsPanel';
import { LegendCard } from './LegendCard';
import { ScheduleCard } from './ScheduleCard';
import { ServiceInfoCard } from './ServiceInfoCard';
import { MapToolbar } from './MapToolbar';
import { PiPCircuitView } from './PiPCircuitView';
import { NOWCAST_OFFSETS } from './nowcastingConstants';
import { DockedPlayerPanel } from './DockedPlayerPanel';
import { StationObservationsPanel } from './StationObservationsPanel';
import { StationsComparisonPanel } from './StationsComparisonPanel';
import { usePiPMap } from './usePiPMap';
import { useSplitMap } from './useSplitMap';
import { usePlayback } from './usePlayback';
import { useDataFetching, type RadarTile } from './useDataFetching';
import { useMapOverlays } from './useMapOverlays';
import { useTioLayer } from './useTioLayer';
import { useNowcastingState, isTioProvider } from './useNowcastingState';
import { resolveMapStyle } from './nowcastingMapIds';
import { useMapInitialization } from './useMapInitialization';
import { useCircuitLayer } from './useCircuitLayer';
import { useCardDragging } from './useCardDragging';
import { useRadarLayer } from './useRadarLayer';
import { useNumericRadarLayer } from './useNumericRadarLayer';
import { useTioTimestamps } from './useTioTimestamps';
import { useEventChangeCleanup } from './useEventChangeCleanup';
import { useStormTrackingLayer } from './useStormTrackingLayer';
import { useRainPathLayer } from './useRainPathLayer';
import { MapAlerts } from './MapAlerts';
import { MapSplitter } from './MapSplitter';
import { SplitMapView } from './SplitMapView';
import { RainForecastCard } from '../../components/Forecast';
import { useObservationFields } from '../../hooks/useObservationFields';
import { usePermissions } from '../../hooks/usePermissions';
import { useStationObservations } from '../../contexts/useStationObservations';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../../utils/constants';
import { getTioTileUrlTemplate, latLonToTile } from '../../api/tomorrowIoTilesService';
import { TileContextMenu } from './TileContextMenu';
import { NowcastingTileViewer, type NowcastingViewerTile } from './NowcastingTileViewer';
import radarTilesService from '../../api/radarTilesService';
import { RadarPreloader, buildPreloadPriorities } from './radarPreloader';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const NowcastingPage = () => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { setSelectedTimestamp } = useSelectedTimestamp();
  const { getVisibleFields, loading: isLoadingObservationFields } = useObservationFields();
  const { isRoot } = usePermissions();
  const { setExternalStations, setSelectedDateRange: setObservationsContextDateRange } = useStationObservations();
  
  // Check if observations are enabled (default to true for backward compatibility)
  const observationsEnabled = selectedEvent?.observations_enabled !== false;
  const tioMapSupported = isTioMapProviderSupported(selectedEvent);
  
  // Set to true to enable weather field selection for Tomorrow.io tiles
  const showTioFieldSelector = false;

  // Centralized state management hook
  const state = useNowcastingState();
  const {
    // Tile State
    tiles, setTiles, currentIndex, setCurrentIndex,
    radarL2NowcastTiles, setRadarL2NowcastTiles,
    // Playback State
    isPlaying, setIsPlaying, fps, setFps, autoLoop, setAutoLoop,
    playbackDock, setPlaybackDock,
    // Radar/Map Layer State
    radarOpacity, setRadarOpacity, mapStyle, setMapStyle,
    mapBasemapThemeOverride, setMapBasemapThemeOverride,
    styleVersion, setStyleVersion, showWindArrows, setShowWindArrows,
    showStormTracking, setShowStormTracking,
    showRainPath, setShowRainPath,
    // Tomorrow.io State
    mapTileProvider, setMapTileProvider, tioTileField, setTioTileField,
    tioTimestamps, setTioTimestamps, tioCurrentIndex, setTioCurrentIndex,
    isTioPlaying, setIsTioPlaying, isRainOnlyMode, toggleRainOnlyMode,
    // Live Mode Time Range
    liveStartMinutes, setLiveStartMinutes, liveEndMinutes, setLiveEndMinutes,
    liveEndMinutesForRadar, setLiveEndMinutesForRadar,
    // Visibility Toggles
    isStationsVisible, setIsStationsVisible,
    isStationsComparisonVisible, setIsStationsComparisonVisible,
    isServiceInfoVisible, setIsServiceInfoVisible,
    isRainForecastVisible, setIsRainForecastVisible,
    isTioForecastVisible, setIsTioForecastVisible,
    isMinutelyForecastVisible, setIsMinutelyForecastVisible,
    isScheduleVisible, setIsScheduleVisible,
    isLegendVisible, setIsLegendVisible,
    isMapSettingsVisible, setIsMapSettingsVisible,
    isPiPVisible, setIsPiPVisible,
    isCircleOverlayVisible, setIsCircleOverlayVisible,
    showRadarBbox, setShowRadarBbox,
    isSplitViewEnabled, setIsSplitViewEnabled,
    splitPosition, setSplitPosition,
    // Radius Circles Settings
    showRadiusCircles, setShowRadiusCircles,
    radiusCircleCount, setRadiusCircleCount,
    radiusCircleDistance, setRadiusCircleDistance,
    // Track Line Width
    trackLineWidth, setTrackLineWidth,
    // Numeric Radar Settings
    numericNoiseFloor, setNumericNoiseFloor,
    numericColormap, setNumericColormap,
    // Card Positions & Dragging
    cardPositions, setCardPositions, draggingCard, setDraggingCard, dragOffset, setDragOffset,
    // PiP Size
    pipSize, setPipSize,
    stationsComparisonSize, setStationsComparisonSize,
    // Map Refs
    mapContainerRef, mapRef,
    // Split Map Refs
    splitMapContainerRef, splitMapRef,
    // Draggable Card Refs
    stationsCardRef,
    stationsComparisonCardRef, serviceInfoCardRef, rainForecastCardRef, tioForecastCardRef, minutelyForecastCardRef,
    legendCardRef, scheduleCardRef, pipCardRef,
    // Radar Layer IDs & Refs
    radarSourceId, radarLayerId, radarSourceId2, radarLayerId2,
    // Numeric Radar Layer IDs & Refs
    radarNumericSourceId, radarNumericLayerId,
    radarNumericSourceId2, radarNumericLayerId2,
    // Tomorrow.io Layer IDs & Refs
    tioSourceId, tioLayerId, tioSourceId2, tioLayerId2,
    tioActiveLayerRef, previousTioTimestampsRef, previousDateRangeRef, tioTilePreloadRef,
    // Caching & Event Tracking Refs
    imageCacheRef, previousEventRef,
    // Reset Function
    resetState,
    resetCardPositions,
  } = state;

  const mapBasemapTheme: Theme = mapBasemapThemeOverride ?? theme;

  // Check if the event is currently active (ongoing) - Tomorrow.io API only works for active events
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  // Poll metadata timestamps to detect external forecast updates
  const metadataTimestamps = useCollectionFreshness(['forecast_daily', 'forecast_hourly', 'forecast_rain'], {
    interval: 30_000,
    enabled: isEventActive,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastNowcastFetchTimeRef = useRef<string>('');

  useEffect(() => {
    const latest = metadataTimestamps.forecast_daily || metadataTimestamps.forecast_hourly || metadataTimestamps.forecast_rain;
    if (latest && latest > lastNowcastFetchTimeRef.current) {
      lastNowcastFetchTimeRef.current = latest;
      setRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  // Tomorrow.io supports historical data up to 7 days back — allow root users to use TIO sources
  // on recently-ended events for debugging purposes
  const isTioAllowed = useMemo(() => {
    if (isEventActive) return true;
    if (!isRoot || !selectedEvent) return false;
    const tz = selectedEvent.circuit?.time_zone || 'UTC';
    const endDate = dayjs(selectedEvent.end_date).tz(tz).endOf('day');
    const now = dayjs().tz(tz);
    return now.diff(endDate, 'day') <= 7;
  }, [isEventActive, isRoot, selectedEvent]);

  // Track live mode from Timeline (for dock button visibility - only show when live mode is ON)
  const [isLiveMode, setIsLiveMode] = useState(false);

  const [timelineCompact, setTimelineCompact] = useState(() => {
    try { return localStorage.getItem('timeline-compact-nowcasting') === 'true'; } catch { return false; }
  });
  const handleTimelineCompactChange = useCallback((v: boolean) => {
    setTimelineCompact(v);
    try { localStorage.setItem('timeline-compact-nowcasting', String(v)); } catch { /* noop */ }
  }, []);

  const [playerCompact, setPlayerCompact] = useState(() => {
    try { return localStorage.getItem('player-compact-nowcasting') === 'true'; } catch { return false; }
  });
  const handlePlayerCompactChange = useCallback((v: boolean) => {
    setPlayerCompact(v);
    try { localStorage.setItem('player-compact-nowcasting', String(v)); } catch { /* noop */ }
  }, []);

  const [legendCompact, setLegendCompact] = useState(() => {
    try { return localStorage.getItem('legend-compact-nowcasting') === 'true'; } catch { return false; }
  });
  const handleLegendCompactChange = useCallback((v: boolean) => {
    setLegendCompact(v);
    try { localStorage.setItem('legend-compact-nowcasting', String(v)); } catch { /* noop */ }
  }, []);

  // Fullscreen map view - pop out map area to cover entire screen (like AllStationsComparison)
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);

  // Right-click tile context menu (root-only debugging)
  const [tileContextMenu, setTileContextMenu] = useState<{
    visible: boolean;
    position: { x: number; y: number };
    tileUrl: string | null;
    tileCoords: { z: number; x: number; y: number } | null;
    lngLat: { lng: number; lat: number } | null;
  }>({ visible: false, position: { x: 0, y: 0 }, tileUrl: null, tileCoords: null, lngLat: null });

  // Nowcasting tile viewer modal (root-only debugging)
  const [nowcastingViewerOpen, setNowcastingViewerOpen] = useState(false);
  const [nowcastingViewerTiles, setNowcastingViewerTiles] = useState<NowcastingViewerTile[]>([]);
  const [nowcastingViewerLoading, setNowcastingViewerLoading] = useState(false);

  // Ref with current tile's datetime (ms) - for 'current' dock: stay on same tile on refresh
  const currentTileDatetimeMsRef = useRef<number | null>(null);

  // Extend tiles with nowcast tiles for live events when nowcast is enabled
  // For 'radar': nowcast tile URLs are derived from the most recent realtime tile
  // For 'radar-l2': nowcast tiles come from the backend (signed URLs can't be constructed client-side)
  const extendedTiles = useMemo(() => {
    if (tiles.length === 0 || !isEventActive || liveEndMinutesForRadar === 0) return tiles;

    if (mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') {
      // L2 nowcast tiles are pre-fetched from the backend with valid signed URLs
      if (radarL2NowcastTiles.length === 0) return tiles;
      return [...tiles, ...radarL2NowcastTiles];
    }

    if (mapTileProvider === 'radar') {
      const lastTile = tiles[tiles.length - 1];
      // Derive nowcast tile URLs from the last realtime tile
      // URL format: .../20251227140000.png -> .../20251227140000+05.png
      const nowcastTiles: RadarTile[] = NOWCAST_OFFSETS.map(offset => ({
        timestamp: lastTile.timestamp,
        url: lastTile.url.replace('.png', `+${String(offset).padStart(2, '0')}.png`),
        datetime: lastTile.datetime.add(offset, 'minute'),
        isNowcast: true,
        forecastOffset: offset,
      }));
      return [...tiles, ...nowcastTiles];
    }

    return tiles;
  }, [tiles, isEventActive, liveEndMinutesForRadar, mapTileProvider, radarL2NowcastTiles]);

  // Playback hook - manages animation loops and playback controls
  const {
    handlePlayPause,
    handleTioPlayPause,
    handleBackward,
    handleForward,
    hasManuallyPausedRef,
    hasManuallyPausedTioRef,
    currentIndexRef,
    tioCurrentIndexRef,
  } = usePlayback({
    isPlaying,
    setIsPlaying,
    currentIndex,
    setCurrentIndex,
    extendedTilesLength: extendedTiles.length,
    tilesLength: tiles.length,
    isTioPlaying,
    setIsTioPlaying,
    tioCurrentIndex,
    setTioCurrentIndex,
    tioTimestampsLength: tioTimestamps.length,
    fps,
    autoLoop,
    isEventActive,
    mapTileProvider,
  });

  // Data fetching hook - manages API calls and data state
  const {
    circuitData,
    rainForecasts,
    minutelyForecasts,
    infos,
    currentInfoIndex,
    stations,
    selectedStationIndex,
    selectedDateRange,
    loading,
    error,
    tilesSource,
    setCurrentInfoIndex,
    setSelectedStationIndex,
    setStations,
    setSelectedDateRange,
    setRadarActivity,
    setStationActivity,
    setDailyForecasts,
    setHourlyForecasts,
    setRainForecasts,
    setMinutelyForecasts,
    setInfos,
    setError,
    radarActivityAvailability,
    stationActivityAvailability,
    forecastAvailability,
    handleDateRangeChange,
  } = useDataFetching({
    selectedEvent,
    observationsEnabled,
    isEventActive,
    autoLoop,
    isPlaying,
    mapTileProvider,
    playbackDock,
    currentTileDatetimeMsRef,
    setIsPlaying,
    setIsTioPlaying,
    setSelectedTimestamp,
    setCurrentIndex,
    setTiles,
    setRadarL2NowcastTiles,
    hasManuallyPausedRef,
    currentIndexRef,
    refreshCounter,
  });

  // Wrap handleDateRangeChange so the context's date range is set synchronously
  // (selectedTimestamp is set immediately in the global context by useDataFetching;
  //  the observations context's selectedDateRange must be set in the same tick
  //  so that the guard in fetchStationObservations sees it before it runs)
  const wrappedHandleDateRangeChange = useCallback((startDate: Date, endDate: Date) => {
    setObservationsContextDateRange({ start: startDate, end: endDate });
    handleDateRangeChange(startDate, endDate);
  }, [handleDateRangeChange, setObservationsContextDateRange]);

  // Sync stations to StationObservationsContext; date range is synced synchronously above
  useEffect(() => {
    setExternalStations(stations.length > 0 ? stations : null);
    return () => {
      setExternalStations(null);
      setObservationsContextDateRange(null);
    };
  }, [stations, setExternalStations, setObservationsContextDateRange]);

  // Handle map tile provider based on event state and observations availability
  // Priority: observationsEnabled check takes precedence over isEventActive check
  // to avoid infinite loop when both conditions would conflict
  const eventSource = selectedEvent ? getEventSource(selectedEvent) : 'prod';

  useEffect(() => {
    // Observations disabled: use T.io overlay only for active or recently-ended events (≤14 days).
    if (!observationsEnabled && (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric')) {
      setMapTileProvider(mapTileProviderForObservationsState(selectedEvent, observationsEnabled));
      setTioCurrentIndex(0);
      return;
    }
    
    // Second priority: Legacy events only have tiles in the old pipeline bucket,
    // so force the provider to 'radar' (which queries legacy_radar_tiles via the
    // secured proxy). L2 endpoints have no data for legacy events.
    if (eventSource === 'legacy' && (mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && observationsEnabled) {
      setMapTileProvider('radar');
      setCurrentIndex(0);
      return;
    }

    // MeteoPress / old L3 source is removed for current events. Leftover UI state
    // (root "Radar (MP)" selection) must not keep calling GET /api/radar-tiles.
    if (eventSource !== 'legacy' && mapTileProvider === 'radar' && observationsEnabled) {
      setMapTileProvider('radar-l2');
      setCurrentIndex(0);
      return;
    }

    // Third priority: Reset to radar-l2 if TIO is not allowed and Tomorrow.io is selected
    // (Tomorrow.io API only works for active events, or recent events for root users)
    // Only do this if observations ARE enabled (otherwise we'd loop back to tomorrow.io)
    if (!isTioAllowed && isTioProvider(mapTileProvider) && observationsEnabled) {
      setMapTileProvider('radar-l2');
      setCurrentIndex(0);
    }

    // Stale events: do not keep a T.io provider selected
    if (!tioMapSupported && isTioProvider(mapTileProvider)) {
      setMapTileProvider('radar-l2');
      setCurrentIndex(0);
      setTioCurrentIndex(0);
    }
  }, [isTioAllowed, mapTileProvider, observationsEnabled, eventSource, selectedEvent, tioMapSupported, setCurrentIndex, setMapTileProvider, setTioCurrentIndex]);

  // Pause playback when switching between sources
  useEffect(() => {
    // Pause radar playback if switching away from a radar source
    if (mapTileProvider !== 'radar' && mapTileProvider !== 'radar-l2' && mapTileProvider !== 'radar-l2-numeric' && isPlaying) {
      setIsPlaying(false);
      hasManuallyPausedRef.current = true; // Mark as manually paused so auto-play doesn't restart
    }

    // Pause Tomorrow.io playback if switching away from Tomorrow.io
    if (!isTioProvider(mapTileProvider) && isTioPlaying) {
      setIsTioPlaying(false);
    }
  }, [mapTileProvider, isPlaying, isTioPlaying, hasManuallyPausedRef, setIsPlaying, setIsTioPlaying]);

  // Re-fetch tiles when the source provider changes (radar <-> radar-l2 <-> tomorrow.io)
  const prevProviderForRefetchRef = useRef(mapTileProvider);
  useEffect(() => {
    const providerChanged = prevProviderForRefetchRef.current !== mapTileProvider;
    prevProviderForRefetchRef.current = mapTileProvider;

    if (providerChanged && selectedDateRange) {
      wrappedHandleDateRangeChange(selectedDateRange.start, selectedDateRange.end);
    }
  }, [mapTileProvider, selectedDateRange, wrappedHandleDateRangeChange]);

  // Event change cleanup hook - clears state and map layers when event changes
  useEventChangeCleanup({
    selectedEvent,
    setSelectedDateRange,
    setError,
    setRadarActivity,
    setStationActivity,
    setDailyForecasts,
    setHourlyForecasts,
    setRainForecasts,
    setMinutelyForecasts,
    setInfos,
    setCurrentInfoIndex,
    setStations,
    setSelectedStationIndex,
    setSelectedTimestamp,
    hasManuallyPausedRef,
    hasManuallyPausedTioRef,
    resetState,
    mapRef,
    radarLayerId,
    radarSourceId,
    radarLayerId2,
    radarSourceId2,
    tioLayerId,
    tioSourceId,
    tioLayerId2,
    tioSourceId2,
    previousEventRef,
  });

  // Prioritised radar tile preloader: current frame first, then next 5,
  // then loop boundaries, then remaining by distance. Concurrency-limited
  // and cancelled/re-scheduled when tiles or currentIndex change.
  const preloaderRef = useRef<RadarPreloader | null>(null);

  // Create / destroy preloader with the LRU cache
  useEffect(() => {
    preloaderRef.current = new RadarPreloader(imageCacheRef.current, 4);
    return () => {
      preloaderRef.current?.destroy();
      preloaderRef.current = null;
    };
  }, [imageCacheRef]);

  // Schedule / reprioritise preloading when tiles or playback position change.
  // When tiles become empty the preloader is cancelled; otherwise the queue
  // is (re-)built with priorities centred on currentIndex.
  useEffect(() => {
    if (extendedTiles.length === 0) {
      preloaderRef.current?.cancel();
      return;
    }
    preloaderRef.current?.resume();
    preloaderRef.current?.schedule(buildPreloadPriorities(extendedTiles, currentIndex));
  }, [extendedTiles, currentIndex]);

  // Reset info index when date range changes
  useEffect(() => {
    if (selectedDateRange) {
      setCurrentInfoIndex(0);
    }
  }, [selectedDateRange, setCurrentInfoIndex]);

  // Map initialization hook - creates Mapbox map with controls and zoom indicator
  const { mapStyles } = useMapInitialization({
    selectedEvent,
    mapStyle,
    theme: mapBasemapTheme,
    mapContainerRef,
    mapRef,
    setStyleVersion,
  });

  // Numeric radar layer hook - decodes .pngd tiles, colorizes client-side, then transitions
  const { currentBlobUrl: numericBlobUrl } = useNumericRadarLayer({
    mapRef,
    selectedEvent,
    extendedTiles,
    currentIndex,
    radarOpacity,
    fps,
    styleVersion,
    mapTileProvider,
    numericNoiseFloor,
    numericColormap,
    radarNumericSourceId,
    radarNumericLayerId,
    radarNumericSourceId2,
    radarNumericLayerId2,
    imageCacheRef,
  });

  // PiP map hook - manages Picture-in-Picture map view
  const { pipMapContainerRef, pipMapRef, pipStyleVersion } = usePiPMap({
    selectedEvent,
    isPiPVisible,
    mapStyle,
    mapStyles,
    theme: mapBasemapTheme,
    circuitData,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    extendedTiles,
    mapTileProvider,
    tioTimestamps,
    tioCurrentIndex,
    tioTileField,
    radarOpacity,
    fps,
    tilesSource,
    trackLineWidth,
    numericBlobUrl,
    isCircleOverlayVisible,
    showRadarBbox,
    showRadiusCircles,
    radiusCircleCount,
    radiusCircleDistance,
  });

  // Split map hook - manages split view secondary map (left map)
  const { 
    handleCenterOnCircuit: handleSplitCenterOnCircuit, 
    handleResetMapView: handleSplitResetMapView,
    splitStyleVersion,
  } = useSplitMap({
    selectedEvent,
    isSplitViewEnabled,
    splitPosition,
    mapStyle,
    mapStyles,
    theme: mapBasemapTheme,
    circuitData,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    extendedTiles,
    mapTileProvider,
    tioTimestamps,
    tioCurrentIndex,
    tioTileField,
    radarOpacity,
    fps,
    tilesSource,
    isCircleOverlayVisible,
    showRadarBbox,
    showRadiusCircles,
    radiusCircleCount,
    radiusCircleDistance,
    trackLineWidth,
    splitMapContainerRef,
    splitMapRef,
    numericBlobUrl,
  });

  // Create a ref for the split view container for the splitter
  const splitViewContainerRef = useRef<HTMLDivElement>(null);

  // Resize main map when split position changes or when switching between split/single view
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current?.resize();
      }, 0);
    }
  }, [splitPosition, isSplitViewEnabled, mapRef]);

  // Resize maps when entering/exiting fullscreen or when window resizes in fullscreen (debounced)
  useEffect(() => {
    const resizeMaps = () => {
      mapRef.current?.resize();
      splitMapRef.current?.resize();
    };

    if (!isMapFullScreen) {
      // Resize when exiting fullscreen so maps fit the restored container
      const timer = setTimeout(resizeMaps, 100);
      return () => clearTimeout(timer);
    }

    // Initial resize when entering fullscreen - delay to ensure DOM has updated with new dimensions
    resizeMaps();
    const initialTimeoutId = setTimeout(resizeMaps, 100);

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedResize = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        resizeMaps();
      }, 200);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(initialTimeoutId);
      window.removeEventListener('resize', debouncedResize);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [isMapFullScreen, mapRef, splitMapRef]);

  // Map overlays hook - manages wind arrows, circuit circle overlay, and radius circles
  useMapOverlays({
    mapRef,
    selectedEvent,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    styleVersion,
    isCircleOverlayVisible,
    showRadarBbox,
    showRadiusCircles,
    radiusCircleCount,
    radiusCircleDistance,
  });

  // Storm tracking layer hook - displays storm direction arrow and track points
  // Compute current timestamp in UTC for syncing with radar playback
  // The tile's datetime is in local timezone, so we convert back to UTC for the API
  const currentRadarTimestampUtc = useMemo(() => {
    if (tiles.length === 0 || currentIndex < 0 || currentIndex >= tiles.length) {
      return null;
    }
    // Convert local datetime back to UTC and format for API
    return tiles[currentIndex].datetime.utc().format('YYYYMMDDHHmmss');
  }, [tiles, currentIndex]);

  useStormTrackingLayer({
    mapRef,
    selectedEvent,
    showStormTracking,
    featureEnabled: isRoot,
    selectedDateRange,
    currentTimestampUtc: currentRadarTimestampUtc,
    styleVersion,
  });

  useRainPathLayer({
    mapRef,
    selectedEvent,
    showRainPath,
    featureEnabled: true,
    selectedDateRange,
    currentTimestampUtc: currentRadarTimestampUtc,
    styleVersion,
  });

  // Storm tracking + rain path on PiP map
  useStormTrackingLayer({
    mapRef: pipMapRef,
    selectedEvent,
    showStormTracking,
    featureEnabled: isRoot && isPiPVisible,
    selectedDateRange,
    currentTimestampUtc: currentRadarTimestampUtc,
    styleVersion: pipStyleVersion,
  });

  useRainPathLayer({
    mapRef: pipMapRef,
    selectedEvent,
    showRainPath,
    featureEnabled: isPiPVisible,
    selectedDateRange,
    currentTimestampUtc: currentRadarTimestampUtc,
    styleVersion: pipStyleVersion,
  });

  // Storm tracking + rain path on split map
  useStormTrackingLayer({
    mapRef: splitMapRef,
    selectedEvent,
    showStormTracking,
    featureEnabled: isRoot && isSplitViewEnabled,
    selectedDateRange,
    currentTimestampUtc: currentRadarTimestampUtc,
    styleVersion: splitStyleVersion,
  });

  useRainPathLayer({
    mapRef: splitMapRef,
    selectedEvent,
    showRainPath,
    featureEnabled: isSplitViewEnabled,
    selectedDateRange,
    currentTimestampUtc: currentRadarTimestampUtc,
    styleVersion: splitStyleVersion,
  });

  // Circuit layer hook - draws circuit track and weather stations
  useCircuitLayer({
    mapRef,
    circuitData,
    selectedEvent,
    styleVersion,
    trackLineWidth,
  });

  // Radar layer hook - manages radar tile overlay with double-buffering
  useRadarLayer({
    mapRef,
    selectedEvent,
    extendedTiles,
    currentIndex,
    radarOpacity,
    fps,
    styleVersion,
    tilesSource,
    mapTileProvider,
    radarSourceId,
    radarLayerId,
    radarSourceId2,
    radarLayerId2,
    imageCacheRef,
    isTioActive: isTioProvider(mapTileProvider),
  });

  // Tomorrow.io timestamps hook - generates timestamps with variable intervals
  useTioTimestamps({
    selectedDateRange,
    setTioTimestamps,
    setTioCurrentIndex,
    tioCurrentIndexRef,
    previousTioTimestampsRef,
    previousDateRangeRef,
    enabled: tioMapSupported,
    isEventActive,
    autoLoop,
    playbackDock,
  });

  // Tomorrow.io tile layer management (crossfade transitions, tile preloading, cleanup)
  useTioLayer({
    mapRef,
    enabled: tioMapSupported,
    tioSourceId,
    tioLayerId,
    tioSourceId2,
    tioLayerId2,
    tioActiveLayerRef,
    mapTileProvider,
    tioTimestamps,
    tioCurrentIndex,
    tioTileField,
    selectedEvent,
    radarOpacity,
    styleVersion,
    tioTilePreloadRef,
  });

  const handlePreviousInfo = useCallback(() => {
    setCurrentInfoIndex((prev) => Math.max(0, prev - 1));
  }, [setCurrentInfoIndex]);

  const handleNextInfo = useCallback((maxLength: number) => {
    setCurrentInfoIndex((prev) => Math.min(maxLength - 1, prev + 1));
  }, [setCurrentInfoIndex]);

  const handleMapStyleChange = useCallback((newStyle: string | null) => {
    setMapStyle(newStyle);
    if (mapRef.current) {
      const map = mapRef.current;
      const center = map.getCenter();
      const zoom = map.getZoom();
      const bearing = map.getBearing();
      const pitch = map.getPitch();

      const styleUrl = resolveMapStyle(newStyle, mapStyles, mapBasemapTheme);

      // Change the style
      map.setStyle(styleUrl);

      // Wait for style to load, then restore view and trigger layer re-render
      map.once('style.load', () => {
        map.jumpTo({ center, zoom, bearing, pitch });
        // Ensure projection is set to mercator after style change
        map.setProjection('mercator');

        // Increment styleVersion to trigger useEffects that redraw circuit and radar layers
        setStyleVersion(v => v + 1);
      });
    }
  }, [mapBasemapTheme, mapStyles, mapRef, setMapStyle, setStyleVersion]);

  const handleResetMapView = useCallback(() => {
    if (mapRef.current && selectedEvent) {
      const circuitLat = selectedEvent.circuit.latitude || 0;
      const circuitLon = selectedEvent.circuit.longitude || 0;
      mapRef.current.flyTo({
        center: [circuitLon, circuitLat],
        zoom: 13,
        bearing: 0,
        pitch: 0,
      });
    }
  }, [selectedEvent, mapRef]);

  const handleCenterOnCircuit = useCallback(() => {
    if (mapRef.current && selectedEvent) {
      const circuitLat = selectedEvent.circuit.latitude || 0;
      const circuitLon = selectedEvent.circuit.longitude || 0;
      mapRef.current.flyTo({
        center: [circuitLon, circuitLat],
      });
    }
  }, [selectedEvent, mapRef]);

  // Card dragging hook - manages draggable card behavior and PiP positioning
  const { handleCardMouseDown } = useCardDragging({
    mapContainerRef,
    cardPositions,
    setCardPositions,
    draggingCard,
    setDraggingCard,
    dragOffset,
    setDragOffset,
    isPiPVisible,
  });

  const currentTile = extendedTiles[currentIndex];

  // Keep currentTileDatetimeMsRef updated for 'current' dock behavior (radar only)
  useEffect(() => {
    if ((mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && currentTile) {
      currentTileDatetimeMsRef.current = currentTile.datetime.valueOf();
    } else {
      currentTileDatetimeMsRef.current = null;
    }
  }, [mapTileProvider, currentTile, currentTileDatetimeMsRef]);

  // Current tile URL for root-only debug button (radar: image URL, tomorrow.io: tile URL template)
  const currentTileUrl = useMemo(() => {
    if ((mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && currentTile) {
      return currentTile.url;
    }
    if (isTioProvider(mapTileProvider) && tioTimestamps.length > 0 && tioTimestamps[tioCurrentIndex]) {
      const excludedLayers = mapTileProvider === 'tomorrow.io-global' ? 'FIA' : undefined;
      return getTioTileUrlTemplate(tioTileField, tioTimestamps[tioCurrentIndex], 'png', excludedLayers);
    }
    return null;
  }, [mapTileProvider, currentTile, tioTimestamps, tioCurrentIndex, tioTileField]);

  const handleViewNowcasting = useCallback(() => {
    if (!currentTile) return;

    const circuitTz = selectedEvent?.circuit?.time_zone || 'UTC';
    const formatTime = (dt: dayjs.Dayjs) => dt.tz(circuitTz).format('HH:mm:ss');

    const baseTile: NowcastingViewerTile = {
      url: currentTile.url,
      label: `${currentTile.datetime.tz(circuitTz).format('YYYY-MM-DD HH:mm:ss')}`,
      isNowcast: false,
    };

    if (mapTileProvider === 'radar') {
      // Show the old radar nowcast tiles (direct GCS URLs) + L2 nowcast tiles for comparison
      const radarNowcastTiles: NowcastingViewerTile[] = NOWCAST_OFFSETS.map(offset => {
        const nowcastUrl = currentTile.url.replace('.png', `+${String(offset).padStart(2, '0')}.png`);
        const nowcastTime = currentTile.datetime.add(offset, 'minute');
        return {
          url: nowcastUrl,
          label: `${formatTime(nowcastTime)}`,
          isNowcast: true,
          forecastOffset: offset,
          source: 'radar' as const,
        };
      });

      setNowcastingViewerTiles([baseTile, ...radarNowcastTiles]);
      setNowcastingViewerOpen(true);
      setNowcastingViewerLoading(true);

      const baseTimestamp = currentTile.timestamp;
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      const endTimestamp = dayjs.utc(baseTimestamp, 'YYYYMMDDHHmmss')
        .add(1, 'minute').format('YYYYMMDDHHmmss');

      radarTilesService.getRadarTilesL2({
        start_timestamp: baseTimestamp,
        end_timestamp: endTimestamp,
      }).then(data => {
        const l2NowcastTiles: NowcastingViewerTile[] = data.tiles
          .filter(t => t.timestamp.startsWith(baseTimestamp + '+'))
          .map(t => {
            const offset = parseInt(t.timestamp.split('+')[1], 10);
            const nowcastTime = currentTile.datetime.add(offset, 'minute');
            let url = t.url;
            if (url.startsWith('/api/')) url = `${apiBaseUrl}${url}`;
            return {
              url,
              label: `${formatTime(nowcastTime)}`,
              isNowcast: true,
              forecastOffset: offset,
              source: 'l2' as const,
            };
          });
        setNowcastingViewerTiles([baseTile, ...radarNowcastTiles, ...l2NowcastTiles]);
      }).catch(err => {
        console.error('Failed to fetch L2 nowcast tiles:', err);
      }).finally(() => {
        setNowcastingViewerLoading(false);
      });
    } else if (mapTileProvider === 'radar-l2') {
      setNowcastingViewerTiles([baseTile]);
      setNowcastingViewerOpen(true);
      setNowcastingViewerLoading(true);

      const baseTimestamp = currentTile.timestamp;
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      const endTimestamp = dayjs.utc(baseTimestamp, 'YYYYMMDDHHmmss')
        .add(1, 'minute').format('YYYYMMDDHHmmss');

      radarTilesService.getRadarTilesL2({
        start_timestamp: baseTimestamp,
        end_timestamp: endTimestamp,
      }).then(data => {
        const nowcastTiles: NowcastingViewerTile[] = data.tiles
          .filter(t => t.timestamp.startsWith(baseTimestamp + '+'))
          .map(t => {
            const offset = parseInt(t.timestamp.split('+')[1], 10);
            const nowcastTime = currentTile.datetime.add(offset, 'minute');
            let url = t.url;
            if (url.startsWith('/api/')) url = `${apiBaseUrl}${url}`;
            return {
              url,
              label: `${formatTime(nowcastTime)}`,
              isNowcast: true,
              forecastOffset: offset,
              source: 'l2' as const,
            };
          });
        setNowcastingViewerTiles([baseTile, ...nowcastTiles]);
      }).catch(err => {
        console.error('Failed to fetch L2 nowcast tiles:', err);
      }).finally(() => {
        setNowcastingViewerLoading(false);
      });
    }
  }, [currentTile, mapTileProvider, selectedEvent]);

  // Right-click context menu for tile URL debugging (root-only)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isRoot) return;

    const handleContextMenu = (e: mapboxgl.MapMouseEvent) => {
      e.preventDefault();

      const { lng, lat } = e.lngLat;
      const zoom = Math.floor(map.getZoom());
      const { x, y } = latLonToTile(lat, lng, zoom);

      let resolvedUrl: string | null = null;

      if ((mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') && currentTile) {
        resolvedUrl = currentTile.url;
      } else if (isTioProvider(mapTileProvider) && tioTimestamps.length > 0 && tioTimestamps[tioCurrentIndex]) {
        const excludedLayers = mapTileProvider === 'tomorrow.io-global' ? 'FIA' : undefined;
        const template = getTioTileUrlTemplate(tioTileField, tioTimestamps[tioCurrentIndex], 'png', excludedLayers);
        resolvedUrl = template
          .replace('{z}', zoom.toString())
          .replace('{x}', x.toString())
          .replace('{y}', y.toString());
      }

      setTileContextMenu({
        visible: true,
        position: { x: e.originalEvent.clientX, y: e.originalEvent.clientY },
        tileUrl: resolvedUrl,
        tileCoords: { z: zoom, x, y },
        lngLat: { lng, lat },
      });
    };

    map.on('contextmenu', handleContextMenu);
    return () => {
      map.off('contextmenu', handleContextMenu);
    };
  }, [mapRef, isRoot, mapTileProvider, currentTile, tioTimestamps, tioCurrentIndex, tioTileField]);

  // Filter infos to only show messages at or after the selected end time
  const filteredInfos = useMemo(() => {
    if (!selectedDateRange || infos.length === 0) return infos;

    const endTime = new Date(selectedDateRange.end).getTime();
    return infos.filter(info => {
      const infoTime = new Date(info.value_date).getTime();
      return infoTime < endTime;
    });
  }, [infos, selectedDateRange]);

  const currentInfo = filteredInfos[currentInfoIndex];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Map-basemap-aware styles for Mapbox controls (follows map light/dark, not only app theme) */}
      <style>{`
        .mapboxgl-ctrl-group {
          background-color: ${mapBasemapTheme === 'dark' ? '#1f1f1f' : '#fff'} !important;
          box-shadow: ${mapBasemapTheme === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)'} !important;
        }
        .mapboxgl-ctrl-group button {
          background-color: transparent !important;
        }
        .mapboxgl-ctrl-group button:hover {
          background-color: ${mapBasemapTheme === 'dark' ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.05)'} !important;
        }
        .mapboxgl-ctrl-group button + button {
          border-top: 1px solid ${mapBasemapTheme === 'dark' ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.1)'} !important;
        }
        .mapboxgl-ctrl-zoom-in .mapboxgl-ctrl-icon,
        .mapboxgl-ctrl-zoom-out .mapboxgl-ctrl-icon,
        .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
          filter: ${mapBasemapTheme === 'dark' ? 'invert(1)' : 'none'};
        }
        .mapboxgl-ctrl-attrib {
          background-color: ${mapBasemapTheme === 'dark' ? 'rgba(31, 31, 31, 0.8)' : 'rgba(255, 255, 255, 0.8)'} !important;
        }
        .mapboxgl-ctrl-attrib a {
          color: ${mapBasemapTheme === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.75)'} !important;
        }
        .mapboxgl-ctrl-attrib-inner {
          color: ${mapBasemapTheme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)'} !important;
        }
        .mapboxgl-ctrl-attrib-button {
          filter: ${mapBasemapTheme === 'dark' ? 'invert(1)' : 'none'};
        }
      `}</style>
      {/* Header */}
      {/* <div style={{
        padding: '24px 24px 16px 24px',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}><ThunderboltOutlined /> Nowcasting</h1>
          <span style={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)' }}>
            Real-time radar visualization and weather nowcasting.
          </span>
        </div>
      </div> */}

      {/* Content Area */}
      {!selectedEvent ? (
        <div style={{ flex: 1, padding: '0 24px', overflow: 'auto' }}>
          <Card>
            <Empty description="Please select an event to view nowcasting data" />
          </Card>
        </div>
      ) : (
        <>
          {/* Timeline */}
          <div style={{
            flexShrink: 0,
          }}>
            <Timeline
              onDateRangeChange={wrappedHandleDateRangeChange}
              forecastAvailability={forecastAvailability}
              radarActivityAvailability={radarActivityAvailability}
              stationActivityAvailability={stationActivityAvailability}
              liveStartMinutes={liveStartMinutes}
              liveEndMinutes={liveEndMinutes}
              onLiveStartMinutesChange={setLiveStartMinutes}
              onLiveEndMinutesChange={setLiveEndMinutes}
              liveEndMinutesForRadar={liveEndMinutesForRadar}
              onLiveEndMinutesForRadarChange={setLiveEndMinutesForRadar}
              mapTileProvider={mapTileProvider}
              onLiveModeChange={setIsLiveMode}
              compact={timelineCompact}
              onCompactChange={handleTimelineCompactChange}
            />
          </div>

          {/* Docked Player Panel - Source, Time, Playback controls */}
          <DockedPlayerPanel
            selectedDateRange={selectedDateRange}
            observationsEnabled={observationsEnabled}
            tilesCount={tiles.length}
            mapTileProvider={mapTileProvider}
            onTomorrowIoSelect={() => {
              setMapTileProvider('tomorrow.io');
              setTioCurrentIndex(0);
            }}
            onGlobalPrecipSelect={() => {
              setMapTileProvider('tomorrow.io-global');
              setTioCurrentIndex(0);
            }}
            onRadarL2Select={() => {
              setMapTileProvider('radar-l2');
              setCurrentIndex(0);
            }}
            onRadarL2NumericSelect={() => {
              setMapTileProvider('radar-l2-numeric');
              setCurrentIndex(0);
            }}
            isRoot={isRoot}
            isEventActive={isTioAllowed}
            showTioMapProviders={tioMapSupported}
            showTioFieldSelector={showTioFieldSelector}
            tioTileField={tioTileField}
            onTioTileFieldChange={setTioTileField}
            radarOpacity={radarOpacity}
            onRadarOpacityChange={setRadarOpacity}
            tiles={tiles}
            tioTimestamps={tioTimestamps}
            currentIndex={currentIndex}
            tioCurrentIndex={tioCurrentIndex}
            isPlaying={isPlaying}
            isTioPlaying={isTioPlaying}
            fps={fps}
            autoLoop={autoLoop}
            selectedEvent={selectedEvent}
            extendedTiles={extendedTiles}
            currentTile={currentTile}
            onBackward={handleBackward}
            onForward={handleForward}
            onPlayPause={handlePlayPause}
            onTioPlayPause={handleTioPlayPause}
            onCurrentIndexChange={setCurrentIndex}
            onTioCurrentIndexChange={setTioCurrentIndex}
            onFpsChange={setFps}
            onAutoLoopChange={setAutoLoop}
            playbackDock={playbackDock}
            onPlaybackDockChange={setPlaybackDock}
            isLiveMode={isLiveMode}
            loading={loading}
            numericNoiseFloor={numericNoiseFloor}
            onNumericNoiseFloorChange={setNumericNoiseFloor}
            numericColormap={numericColormap}
            onNumericColormapChange={setNumericColormap}
            compact={playerCompact}
            onCompactChange={handlePlayerCompactChange}
          />

          {/* Map Container - Always visible when event is selected. When fullscreen, expands to cover viewport. */}
          <div
            style={{
              ...(isMapFullScreen
                ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 9999,
                    backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                  }
                : {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                  }),
            }}
          >
            {/* Docked panel duplicate inside fullscreen so it remains accessible */}
            {isMapFullScreen && (
              <DockedPlayerPanel
                selectedDateRange={selectedDateRange}
                observationsEnabled={observationsEnabled}
                tilesCount={tiles.length}
                mapTileProvider={mapTileProvider}
                onTomorrowIoSelect={() => {
                  setMapTileProvider('tomorrow.io');
                  setTioCurrentIndex(0);
                }}
                onGlobalPrecipSelect={() => {
                  setMapTileProvider('tomorrow.io-global');
                  setTioCurrentIndex(0);
                }}
                onRadarL2Select={() => {
                  setMapTileProvider('radar-l2');
                  setCurrentIndex(0);
                }}
                onRadarL2NumericSelect={() => {
                  setMapTileProvider('radar-l2-numeric');
                  setCurrentIndex(0);
                }}
                isRoot={isRoot}
                isEventActive={isTioAllowed}
                showTioMapProviders={tioMapSupported}
                showTioFieldSelector={showTioFieldSelector}
                tioTileField={tioTileField}
                onTioTileFieldChange={setTioTileField}
                radarOpacity={radarOpacity}
                onRadarOpacityChange={setRadarOpacity}
                tiles={tiles}
                tioTimestamps={tioTimestamps}
                currentIndex={currentIndex}
                tioCurrentIndex={tioCurrentIndex}
                isPlaying={isPlaying}
                isTioPlaying={isTioPlaying}
                fps={fps}
                autoLoop={autoLoop}
                selectedEvent={selectedEvent}
                extendedTiles={extendedTiles}
                currentTile={currentTile}
                onBackward={handleBackward}
                onForward={handleForward}
                onPlayPause={handlePlayPause}
                onTioPlayPause={handleTioPlayPause}
                onCurrentIndexChange={setCurrentIndex}
                onTioCurrentIndexChange={setTioCurrentIndex}
                onFpsChange={setFps}
                onAutoLoopChange={setAutoLoop}
                playbackDock={playbackDock}
                onPlaybackDockChange={setPlaybackDock}
                isLiveMode={isLiveMode}
                loading={loading}
                numericNoiseFloor={numericNoiseFloor}
                onNumericNoiseFloorChange={setNumericNoiseFloor}
                numericColormap={numericColormap}
                onNumericColormapChange={setNumericColormap}
                compact={playerCompact}
                onCompactChange={handlePlayerCompactChange}
              />
            )}
            <div 
              ref={splitViewContainerRef}
              style={{
                flex: 1,
                position: 'relative',
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
              }}
            >
            {/* Split View - Left Map */}
            {isSplitViewEnabled && (
              <>
                <div style={{ width: `${splitPosition}%`, height: '100%', position: 'relative' }}>
                  <SplitMapView
                    splitMapContainerRef={splitMapContainerRef}
                    onCenterOnCircuit={handleSplitCenterOnCircuit}
                    onResetMapView={handleSplitResetMapView}
                    mapBasemapTheme={mapBasemapTheme}
                  />
                </div>
                <MapSplitter
                  onPositionChange={setSplitPosition}
                  containerRef={splitViewContainerRef}
                />
              </>
            )}

            {/* Main Map (Right map in split view) */}
            <div style={{ 
              width: isSplitViewEnabled ? `${100 - splitPosition}%` : '100%', 
              height: '100%',
              position: 'relative',
            }}>
              <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

              {/* Right Toolbar - Mapbox style buttons (inside main map container) */}
              <MapToolbar
                observationsEnabled={observationsEnabled}
                isScheduleVisible={isScheduleVisible}
                setIsScheduleVisible={setIsScheduleVisible}
                isLegendVisible={isLegendVisible}
                setIsLegendVisible={setIsLegendVisible}
                isRainOnlyMode={isRainOnlyMode}
                toggleRainOnlyMode={toggleRainOnlyMode}
                isPiPVisible={isPiPVisible}
                setIsPiPVisible={setIsPiPVisible}
                isSplitViewEnabled={isSplitViewEnabled}
                setIsSplitViewEnabled={setIsSplitViewEnabled}
                isStationsVisible={isStationsVisible}
                setIsStationsVisible={setIsStationsVisible}
                isStationsComparisonVisible={isStationsComparisonVisible}
                setIsStationsComparisonVisible={setIsStationsComparisonVisible}
                isServiceInfoVisible={isServiceInfoVisible}
                setIsServiceInfoVisible={setIsServiceInfoVisible}
                isRainForecastVisible={isRainForecastVisible}
                setIsRainForecastVisible={setIsRainForecastVisible}
                isTioForecastVisible={isTioForecastVisible}
                setIsTioForecastVisible={setIsTioForecastVisible}
                isMinutelyForecastVisible={isMinutelyForecastVisible}
                setIsMinutelyForecastVisible={setIsMinutelyForecastVisible}
                hasMinutelyForecasts={minutelyForecasts.length > 0}
                isCircleOverlayVisible={isCircleOverlayVisible}
                setIsCircleOverlayVisible={setIsCircleOverlayVisible}
                showRadarBbox={showRadarBbox}
                setShowRadarBbox={setShowRadarBbox}
                isMapSettingsVisible={isMapSettingsVisible}
                setIsMapSettingsVisible={setIsMapSettingsVisible}
                mapTileProvider={mapTileProvider}
                tiles={tiles}
                stations={stations}
                selectedDateRange={selectedDateRange}
                filteredInfos={filteredInfos}
                currentInfo={currentInfo}
                rainForecasts={rainForecasts}
                tioTimestamps={tioTimestamps}
                handleCenterOnCircuit={handleCenterOnCircuit}
                handleResetMapView={handleResetMapView}
                handleResetCardPositions={resetCardPositions}
                isRoot={isRoot}
                currentTileUrl={currentTileUrl}
                onOpenFullScreen={() => setIsMapFullScreen(true)}
                isMapFullScreen={isMapFullScreen}
                onCloseFullScreen={() => setIsMapFullScreen(false)}
              />
            </div>

            {/* Overlays positioned above both maps */}

            {/* Map Alerts - Error, No Data Selected, No Tiles Available */}
            <MapAlerts
              error={error}
              loading={loading}
              selectedDateRange={selectedDateRange}
              mapTileProvider={mapTileProvider}
              tilesLength={tiles.length}
              theme={theme}
              onErrorClose={() => setError(null)}
            />

            {/* Station Observations Panel - Show when stations data is available */}
            {stations.length > 0 && selectedDateRange && isStationsVisible && stations[selectedStationIndex] && (
              <StationObservationsPanel
                ref={stationsCardRef}
                theme={theme}
                stations={stations}
                selectedStationIndex={selectedStationIndex}
                selectedDateRange={selectedDateRange}
                selectedEvent={selectedEvent}
                cardPosition={cardPositions.stations}
                isDragging={draggingCard === 'stations'}
                onMouseDown={(e) => handleCardMouseDown('stations', e, cardPositions.stations, stationsCardRef.current)}
                onClose={() => setIsStationsVisible(false)}
                onStationIndexChange={setSelectedStationIndex}
                getVisibleFields={getVisibleFields}
                isLoadingObservationFields={isLoadingObservationFields}
                currentPlayerTime={currentTile?.datetime?.toDate() ?? null}
              />
            )}

            {/* Stations Comparison Panel - Chart overlay with settings in drawer */}
            {stations.length > 0 && selectedDateRange && isStationsComparisonVisible && (
              <StationsComparisonPanel
                ref={stationsComparisonCardRef}
                theme={theme}
                stations={stations}
                selectedEvent={selectedEvent}
                cardPosition={cardPositions.stationsComparison}
                isDragging={draggingCard === 'stationsComparison'}
                onMouseDown={(e) => handleCardMouseDown('stationsComparison', e, cardPositions.stationsComparison, stationsComparisonCardRef.current)}
                onClose={() => setIsStationsComparisonVisible(false)}
                size={stationsComparisonSize}
                onSizeChange={setStationsComparisonSize}
              />
            )}

            {/* Tomorrow.io Forecast Panel */}
            {isTioProvider(mapTileProvider) && tioTimestamps.length > 0 && isTioForecastVisible && (
              <TioForecastPanel
                theme={theme}
                tioTimestamps={tioTimestamps}
                tioCurrentIndex={tioCurrentIndex}
                selectedEvent={selectedEvent}
                cardPosition={cardPositions.tioForecast}
                isDragging={draggingCard === 'tioForecast'}
                onMouseDown={(e) => handleCardMouseDown('tioForecast', e, cardPositions.tioForecast, tioForecastCardRef.current)}
                onClose={() => setIsTioForecastVisible(false)}
                cardRef={tioForecastCardRef}
              />
            )}

            {/* Minutely Forecast Panel */}
            {isRoot && minutelyForecasts.length > 0 && selectedDateRange && isMinutelyForecastVisible && (
              <MinutelyForecastPanel
                theme={theme}
                minutelyForecasts={minutelyForecasts}
                selectedDateRange={selectedDateRange}
                sessions={selectedEvent?.sessions || []}
                circuitTimeZone={selectedEvent?.circuit?.time_zone || 'UTC'}
                cardPosition={cardPositions.minutelyForecast}
                isDragging={draggingCard === 'minutelyForecast'}
                onMouseDown={(e) => handleCardMouseDown('minutelyForecast', e, cardPositions.minutelyForecast, minutelyForecastCardRef.current)}
                onClose={() => setIsMinutelyForecastVisible(false)}
                cardRef={minutelyForecastCardRef}
              />
            )}

            {/* Service Information Card - Bottom Left */}
            <ServiceInfoCard
              filteredInfos={filteredInfos}
              currentInfo={currentInfo}
              currentInfoIndex={currentInfoIndex}
              isVisible={isServiceInfoVisible}
              cardPosition={cardPositions.serviceInfo}
              isDragging={draggingCard === 'serviceInfo'}
              onMouseDown={(e) => handleCardMouseDown('serviceInfo', e, cardPositions.serviceInfo, serviceInfoCardRef.current)}
              onClose={() => setIsServiceInfoVisible(false)}
              onPrevious={handlePreviousInfo}
              onNext={handleNextInfo}
              cardRef={serviceInfoCardRef}
            />

            {/* Rain Forecast Card - Bottom Right */}
            {rainForecasts.length > 0 && selectedDateRange && isRainForecastVisible && (
              <div
                ref={rainForecastCardRef}
                onMouseDown={(e) => handleCardMouseDown('rainForecast', e, cardPositions.rainForecast, rainForecastCardRef.current)}
                style={{
                  position: 'absolute',
                  ...(cardPositions.rainForecast
                    ? { left: cardPositions.rainForecast.x, top: cardPositions.rainForecast.y }
                    : { bottom: 60, right: 24 }),
                  zIndex: draggingCard === 'rainForecast' ? 2000 : 1000,
                  minWidth: 320,
                  maxWidth: 400,
                  cursor: draggingCard === 'rainForecast' ? 'grabbing' : 'default',
                }}>
                <RainForecastCard
                  forecasts={rainForecasts}
                  selectedDate={selectedDateRange ? selectedDateRange.end : null}
                  onClose={() => setIsRainForecastVisible(false)}
                />
              </div>
            )}

            {/* Picture-in-Picture Circuit View */}
            <PiPCircuitView
              isVisible={isPiPVisible}
              mapTileProvider={mapTileProvider}
              tilesLength={tiles.length}
              tioTimestampsLength={tioTimestamps.length}
              pipCardRef={pipCardRef}
              pipMapContainerRef={pipMapContainerRef}
              cardPosition={cardPositions.pip}
              draggingCard={draggingCard}
              onCardMouseDown={handleCardMouseDown}
              onClose={() => setIsPiPVisible(false)}
              size={pipSize}
              onSizeChange={setPipSize}
              pipMapRef={pipMapRef}
              circuitCenter={selectedEvent ? { lat: selectedEvent.circuit.latitude || 0, lon: selectedEvent.circuit.longitude || 0 } : null}
              mapBasemapTheme={mapBasemapTheme}
            />

            {/* Schedule Card */}
            <ScheduleCard
              isVisible={isScheduleVisible}
              cardPosition={cardPositions.schedule}
              isDragging={draggingCard === 'schedule'}
              onMouseDown={(e) => handleCardMouseDown('schedule', e, cardPositions.schedule, scheduleCardRef.current)}
              onClose={() => setIsScheduleVisible(false)}
              cardRef={scheduleCardRef}
            />

            {/* Legend Card */}
            <LegendCard
              isVisible={isLegendVisible}
              mapTileProvider={mapTileProvider}
              tioTileField={tioTileField}
              isRainOnlyMode={isRainOnlyMode}
              numericColormap={numericColormap}
              compact={legendCompact}
              onCompactChange={handleLegendCompactChange}
              cardPosition={cardPositions.legend}
              isDragging={draggingCard === 'legend'}
              onMouseDown={(e) => handleCardMouseDown('legend', e, cardPositions.legend, legendCardRef.current)}
              onClose={() => setIsLegendVisible(false)}
              cardRef={legendCardRef}
            />

            {/* Map Settings Card */}
            <MapSettingsPanel
              isVisible={isMapSettingsVisible}
              onClose={() => setIsMapSettingsVisible(false)}
              theme={theme}
              mapBasemapThemeOverride={mapBasemapThemeOverride}
              onMapBasemapThemeOverrideChange={setMapBasemapThemeOverride}
              mapStyle={mapStyle}
              mapStyles={mapStyles}
              onMapStyleChange={handleMapStyleChange}
              observationsEnabled={observationsEnabled}
              showWindArrows={showWindArrows}
              onWindArrowsToggle={setShowWindArrows}
              stormTrackingFeatureEnabled={isRoot}
              showStormTracking={showStormTracking}
              onStormTrackingToggle={setShowStormTracking}
              showRainPath={showRainPath}
              onRainPathToggle={setShowRainPath}
              trackLineWidth={trackLineWidth}
              onTrackLineWidthChange={setTrackLineWidth}
              showRadiusCircles={showRadiusCircles}
              onRadiusCirclesToggle={setShowRadiusCircles}
              radiusCircleCount={radiusCircleCount}
              onRadiusCircleCountChange={setRadiusCircleCount}
              radiusCircleDistance={radiusCircleDistance}
              onRadiusCircleDistanceChange={setRadiusCircleDistance}
            />

            {/* Fullscreen close button - visible only when map is in fullscreen */}
            {isMapFullScreen && (
              <Button
                type="primary"
                icon={<FullscreenExitOutlined />}
                onClick={() => setIsMapFullScreen(false)}
                style={{
                  position: 'absolute',
                  bottom: 24,
                  right: 24,
                  zIndex: 10001,
                }}
              >
                Close
              </Button>
            )}

            {/* Right-click tile URL context menu (root-only debugging) */}
            {isRoot && (
              <TileContextMenu
                visible={tileContextMenu.visible}
                position={tileContextMenu.position}
                tileUrl={tileContextMenu.tileUrl}
                tileCoords={tileContextMenu.tileCoords}
                lngLat={tileContextMenu.lngLat}
                onClose={() => setTileContextMenu(prev => ({ ...prev, visible: false }))}
                showNowcastingOption={mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric'}
                onViewNowcasting={handleViewNowcasting}
              />
            )}

            {/* Nowcasting tile viewer modal (root-only debugging) */}
            {isRoot && (
              <NowcastingTileViewer
                open={nowcastingViewerOpen}
                onClose={() => setNowcastingViewerOpen(false)}
                tiles={nowcastingViewerTiles}
                loading={nowcastingViewerLoading}
                title={`Nowcasting Tiles — ${currentTile?.datetime.tz(selectedEvent?.circuit?.time_zone || 'UTC').format('YYYY-MM-DD HH:mm:ss') ?? ''}`}
              />
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
